import { useState, useRef, useCallback, useEffect } from 'react';
import { hapticLight, hapticWarning } from '../utils/haptics';

/**
 * Custom hook for swipe-to-delete functionality on mobile devices
 * @param {Object} options Configuration options
 * @param {Function} options.onDelete Callback when item is deleted
 * @param {number} options.threshold Percentage of width to trigger delete (0-1), default 0.35
 * @param {number} options.maxSwipe Maximum swipe distance as percentage of width, default 0.5
 * @param {boolean} options.disabled Disable swipe functionality
 * @returns {Object} Swipe handlers and state
 */
export function useSwipeToDelete({
  onDelete,
  threshold = 0.35,
  maxSwipe = 0.5,
  disabled = false
} = {}) {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteHint, setShowDeleteHint] = useState(false);
  
  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const currentXRef = useRef(0);
  const containerWidthRef = useRef(0);
  const isHorizontalSwipeRef = useRef(null); // null = undecided, true/false = decided

  // Reset state
  const reset = useCallback(() => {
    setSwipeOffset(0);
    setIsSwiping(false);
    setShowDeleteHint(false);
    isHorizontalSwipeRef.current = null;
  }, []);

  // Handle touch start
  const handleTouchStart = useCallback((e) => {
    if (disabled || isDeleting) return;
    
    const touch = e.touches[0];
    startXRef.current = touch.clientX;
    startYRef.current = touch.clientY;
    currentXRef.current = touch.clientX;
    isHorizontalSwipeRef.current = null;
    
    if (containerRef.current) {
      containerWidthRef.current = containerRef.current.offsetWidth;
    }
  }, [disabled, isDeleting]);

  // Handle touch move
  const handleTouchMove = useCallback((e) => {
    if (disabled || isDeleting) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - startXRef.current;
    const deltaY = touch.clientY - startYRef.current;
    
    // Determine swipe direction on first significant movement
    if (isHorizontalSwipeRef.current === null) {
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);
      
      // Need at least 10px of movement to decide
      if (absDeltaX > 10 || absDeltaY > 10) {
        // It's a horizontal swipe if X movement is greater than Y
        isHorizontalSwipeRef.current = absDeltaX > absDeltaY;
        
        if (isHorizontalSwipeRef.current) {
          setIsSwiping(true);
          hapticLight();
        }
      }
    }
    
    // Only process horizontal swipes
    if (!isHorizontalSwipeRef.current) return;
    
    // Prevent vertical scrolling while swiping
    e.preventDefault();
    
    currentXRef.current = touch.clientX;
    
    // Only allow left swipe (negative deltaX)
    if (deltaX >= 0) {
      setSwipeOffset(0);
      setShowDeleteHint(false);
      return;
    }
    
    // Calculate swipe percentage
    const swipePercent = Math.abs(deltaX) / containerWidthRef.current;
    
    // Limit to maxSwipe
    const limitedPercent = Math.min(swipePercent, maxSwipe);
    const offset = -limitedPercent * containerWidthRef.current;
    
    setSwipeOffset(offset);
    
    // Show delete hint when past threshold
    const pastThreshold = swipePercent >= threshold;
    if (pastThreshold !== showDeleteHint) {
      setShowDeleteHint(pastThreshold);
      if (pastThreshold) {
        hapticWarning();
      }
    }
  }, [disabled, isDeleting, threshold, maxSwipe, showDeleteHint]);

  // Handle touch end
  const handleTouchEnd = useCallback(() => {
    if (disabled || isDeleting || !isSwiping) {
      reset();
      return;
    }
    
    const deltaX = currentXRef.current - startXRef.current;
    const swipePercent = Math.abs(deltaX) / containerWidthRef.current;
    
    // If past threshold, trigger delete
    if (swipePercent >= threshold && deltaX < 0) {
      setIsDeleting(true);
      // Animate off screen
      setSwipeOffset(-containerWidthRef.current);
      
      // Call delete callback after animation
      setTimeout(() => {
        if (onDelete) {
          onDelete();
        }
        setIsDeleting(false);
        reset();
      }, 200);
    } else {
      // Snap back
      reset();
    }
  }, [disabled, isDeleting, isSwiping, threshold, onDelete, reset]);

  // Handle touch cancel
  const handleTouchCancel = useCallback(() => {
    reset();
  }, [reset]);

  // Bind event handlers
  const bindSwipeHandlers = useCallback(() => ({
    ref: containerRef,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchCancel,
  }), [handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  return {
    swipeOffset,
    isSwiping,
    isDeleting,
    showDeleteHint,
    bindSwipeHandlers,
    reset
  };
}

export default useSwipeToDelete;
